import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { AppDispatch, RootState } from '@/store';
import { update } from '@/store/storeSlice';
import { TreeItem } from '@/store/types';
import {
  Button,
  Input,
  Label,
  Select,
  SelectOption,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export type TFormProducts = {
  PRODUCT_ID: TreeItem;
  PRODUCT: TreeItem;
  SITE_ID: TreeItem;
  PRODUCT_CLASS: TreeItem;
};

/**
 * FormProducts Component
 * Manages the form for editing a distinct product details within a simulation scenario
 * Handles form submission, input changes, and closing the form drawer
 *
 * @returns {JSX.Element} - The rendered component
 */
const FormProducts = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected === true),
  );

  const WipArea = 'products';

  const InitialFormProducts: TFormProducts = {
    PRODUCT_ID: { value: '', originalValue: '' },
    PRODUCT: { value: '', originalValue: '' },
    SITE_ID: { value: '', originalValue: '' },
    PRODUCT_CLASS: { value: '', originalValue: '' },
  };

  const [form, setForm] = useState(InitialFormProducts);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { id, value } = event.target as HTMLInputElement & {
      id: string;
      value: string;
      type: string;
      checked: boolean;
    };
    const updateForm = (id: keyof TFormProducts, newValue: unknown) => {
      setForm((prevForm) => ({
        ...prevForm,
        [id]: {
          ...prevForm[id],
          value: newValue,
        },
      }));
    };

    updateForm(id as keyof TFormProducts, value);
  };

  const handleCloseDrawer = () => {
    setSimulationParams(
      produce((draft) => {
        draft.formItem = undefined;
        draft.formArea = undefined;
        draft.drawerOpen = false;
      }),
    );

    setForm(InitialFormProducts);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    dispatch(
      update({
        id: simulationParams.formItem,
        area: WipArea,
        content: form,
      }),
    );

    handleCloseDrawer();
  };

  const selectedItem =
    simulationParams.formItem && selectedScenario
      ? selectedScenario.data[WipArea].byId[simulationParams.formItem]
      : null;

  useEffect(() => {
    if (!selectedItem) return;

    const formToChange: TFormProducts = {
      PRODUCT_ID: {
        value: selectedItem.PRODUCT_ID.value,
        originalValue: selectedItem.PRODUCT_ID.originalValue,
      },
      PRODUCT: {
        value: selectedItem.PRODUCT.value,
        originalValue: selectedItem.PRODUCT.originalValue,
      },
      PRODUCT_CLASS: {
        value: selectedItem.PRODUCT_CLASS.value,
        originalValue: selectedItem.PRODUCT_CLASS.originalValue,
      },
      SITE_ID: {
        value: selectedItem.SITE_ID.value,
        originalValue: selectedItem.SITE_ID.originalValue,
      },
    };
    setForm(formToChange);
  }, [selectedItem]);

  if (!selectedItem || !selectedScenario) return;

  type TSelect = {
    id: string;
    name: string;
    class?: string;
  };

  const sites: TSelect[] = [];
  selectedScenario.data.sites.allIds.forEach((itemId: string) => {
    const item = selectedScenario.data.sites.byId[itemId];

    const newItem = {
      id: item.SITE_ID.originalValue,
      name: item.SITE.value,
    };

    sites.push(newItem);
  });
  const workplans: TSelect[] = [];
  selectedScenario.data.work_plans.allIds.forEach((itemId: string) => {
    const item = selectedScenario.data.work_plans.byId[itemId];

    const newItem = {
      id: item.WORK_PLAN_ID.originalValue,
      name: item.WORK_PLAN.value,
    };

    workplans.push(newItem);
  });

  const products: TSelect[] = [];
  const uniqueClasses = new Set<string>();
  selectedScenario.data.products.allIds.forEach((itemId: string) => {
    const item = selectedScenario.data.products.byId[itemId];

    const newItem = {
      id: item.PRODUCT_ID.originalValue,
      name: item.PRODUCT.value,
      class: item.PRODUCT_CLASS.value,
    };

    products.push(newItem);
    uniqueClasses.add(item.PRODUCT_CLASS.value);
  });

  const formMargin = 30;

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 20,
          paddingBottom: 100,
        }}
      >
        <Typography tag="h4" color="#8a00e5">
          {' '}
          Edit Product
        </Typography>
        <div
          style={{
            marginBottom: formMargin,
            marginTop: formMargin / 2,
          }}
        >
          <div>
            <Label tag="textsmall_strong">End Product</Label>
            <Typography tag="textsmall">
              {selectedScenario.data[WipArea].byId[
                selectedItem.END_PRODUCT_ID.value
              ]
                ? selectedScenario.data[WipArea].byId[
                    selectedItem.END_PRODUCT_ID.value
                  ].PRODUCT.value
                : 'NOT AVAILABLE'}
            </Typography>
          </div>
        </div>
        <Label tag="textsmall_strong">Product</Label>
        <Input
          size="small"
          id="PRODUCT"
          defaultValue={form.PRODUCT.value}
          onChange={(event) => handleInputChange(event)}
        ></Input>
        <Label marginTop={formMargin} tag="textsmall_strong">
          Product Class
        </Label>
        <Select
          onChange={(event) => handleInputChange(event)}
          size="small"
          defaultValue={form.PRODUCT_CLASS.value}
          id="PRODUCT_CLASS"
        >
          {[...uniqueClasses].map((className) => (
            <SelectOption key={className} label={className} value={className} />
          ))}
        </Select>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            marginTop: 30,
            backgroundColor: 'white',
            paddingBottom: 20,
            paddingTop: 20,
            width: `calc(100% - 40px)`,
            borderTop: 'solid 1px #ededed',
          }}
        >
          <Stack direction="row" distribute="space-around">
            <Button
              buttonType="button"
              type="primaryOutline"
              width="200px"
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
            <Button buttonType="submit" type="primaryNoShadow" width="200px">
              Update
            </Button>
          </Stack>
        </div>
      </div>
    </form>
  );
};

export default FormProducts;
